import { useState, useEffect } from 'react'
import { customerApi } from '../services/api'
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ClipboardIcon,
  CheckIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LinkIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'

export default function WhatsAppIntegration() {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [phoneNumberId, setPhoneNumberId] = useState('')
  const [accessToken, setAccessToken] = useState('')
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [disconnecting, setDisconnecting] = useState(false)
  const [testResult, setTestResult] = useState(null)
  const [error, setError] = useState('')
  const [showSetup, setShowSetup] = useState(false)
  const [webhookCopied, setWebhookCopied] = useState(false)
  const [linkCopied, setLinkCopied] = useState(false)

  const WEBHOOK_URL = 'https://chat.cognavia.ai/api/v1/whatsapp/webhook'

  const fetchStatus = async () => {
    try {
      const res = await customerApi.get('/customer/whatsapp/status')
      setStatus(res.data)
    } catch (err) {
      console.error('Error fetching WhatsApp status:', err)
      setStatus({ connected: false, is_active: false })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStatus()
  }, [])

  const handleConnect = async () => {
    if (!phoneNumberId.trim() || !accessToken.trim()) {
      setError('Both Phone Number ID and Access Token are required')
      return
    }
    setError('')
    setSaving(true)
    try {
      await customerApi.post('/customer/whatsapp/config', {
        phone_number_id: phoneNumberId.trim(),
        access_token: accessToken.trim(),
      })
      // Auto-test after saving
      const testRes = await customerApi.post('/customer/whatsapp/test')
      if (testRes.data.success) {
        setTestResult({ success: true, message: testRes.data.message })
      } else {
        setTestResult({ success: false, message: testRes.data.message })
      }
      await fetchStatus()
      setPhoneNumberId('')
      setAccessToken('')
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save configuration')
    } finally {
      setSaving(false)
    }
  }

  const handleTest = async () => {
    setTesting(true)
    setTestResult(null)
    try {
      const res = await customerApi.post('/customer/whatsapp/test')
      setTestResult(res.data)
    } catch (err) {
      setTestResult({ success: false, message: err.response?.data?.detail || 'Test failed' })
    } finally {
      setTesting(false)
    }
  }

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect WhatsApp? Your chatbot will stop responding to WhatsApp messages.')) return
    setDisconnecting(true)
    try {
      await customerApi.delete('/customer/whatsapp/config')
      setStatus({ connected: false, is_active: false })
      setTestResult(null)
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to disconnect')
    } finally {
      setDisconnecting(false)
    }
  }

  const copyToClipboard = (text, setter) => {
    navigator.clipboard.writeText(text)
    setter(true)
    setTimeout(() => setter(false), 2000)
  }

  // Clean phone number for wa.me link (remove + and spaces)
  const getWaMeLink = () => {
    if (!status?.display_phone_number) return null
    const clean = status.display_phone_number.replace(/[^0-9]/g, '')
    return `https://wa.me/${clean}`
  }

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-slate-200 rounded w-1/3" />
          <div className="h-40 bg-slate-200 rounded-xl" />
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-slate-900">WhatsApp Channel</h2>
        <p className="text-sm text-slate-500 mt-1">
          Connect your WhatsApp Business number to let customers chat with your AI chatbot via WhatsApp.
        </p>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="flex items-start space-x-3 bg-red-50 border border-red-200 rounded-xl p-4">
          <ExclamationCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-600 mt-0.5">{error}</p>
          </div>
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">
            <span className="text-lg">&times;</span>
          </button>
        </div>
      )}

      {/* Test Result Alert */}
      {testResult && (
        <div className={`flex items-start space-x-3 border rounded-xl p-4 ${
          testResult.success ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
        }`}>
          {testResult.success
            ? <CheckCircleIcon className="h-5 w-5 text-emerald-500 flex-shrink-0 mt-0.5" />
            : <ExclamationCircleIcon className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
          }
          <div>
            <p className={`text-sm font-medium ${testResult.success ? 'text-emerald-800' : 'text-amber-800'}`}>
              {testResult.success ? 'Connection Successful' : 'Connection Issue'}
            </p>
            <p className={`text-sm mt-0.5 ${testResult.success ? 'text-emerald-600' : 'text-amber-600'}`}>
              {testResult.message}
            </p>
          </div>
          <button onClick={() => setTestResult(null)} className="ml-auto text-slate-400 hover:text-slate-600">
            <span className="text-lg">&times;</span>
          </button>
        </div>
      )}

      {/* Main Card */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {/* Card Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <div className="flex items-center space-x-4">
            {/* WhatsApp Icon */}
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <svg className="h-7 w-7 text-green-600" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
            </div>
            <div>
              <h3 className="text-base font-semibold text-slate-900">WhatsApp Business</h3>
              <p className="text-xs text-slate-500">Receive and respond to WhatsApp messages with your AI chatbot</p>
            </div>
          </div>
          {/* Status Badge */}
          {status?.connected ? (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
              <span className="w-2 h-2 bg-green-500 rounded-full mr-2" />
              Connected
            </span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-600">
              <span className="w-2 h-2 bg-gray-400 rounded-full mr-2" />
              Not Connected
            </span>
          )}
        </div>

        {/* Card Body */}
        <div className="p-6">
          {status?.connected ? (
            /* ==================== CONNECTED STATE ==================== */
            <div className="space-y-5">
              {/* Connection Info */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className="text-xs text-green-700 font-medium">Phone Number</p>
                    <p className="text-sm text-green-900 font-semibold mt-0.5">
                      {status.display_phone_number || status.phone_number_id}
                    </p>
                  </div>
                  {status.business_name && (
                    <div>
                      <p className="text-xs text-green-700 font-medium">Business Name</p>
                      <p className="text-sm text-green-900 font-semibold mt-0.5">{status.business_name}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* wa.me Link */}
              {getWaMeLink() && (
                <div className="flex items-center justify-between bg-slate-50 rounded-xl p-4">
                  <div>
                    <p className="text-xs text-slate-500 font-medium">WhatsApp Chat Link</p>
                    <a
                      href={getWaMeLink()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-brand-600 hover:text-brand-700 font-medium flex items-center space-x-1 mt-0.5"
                    >
                      <span>{getWaMeLink()}</span>
                      <LinkIcon className="h-3.5 w-3.5" />
                    </a>
                  </div>
                  <button
                    onClick={() => copyToClipboard(getWaMeLink(), setLinkCopied)}
                    className="px-3 py-1.5 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center space-x-1"
                  >
                    {linkCopied ? <CheckIcon className="h-3.5 w-3.5 text-green-500" /> : <ClipboardIcon className="h-3.5 w-3.5" />}
                    <span>{linkCopied ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={handleTest}
                  disabled={testing}
                  className="px-4 py-2 text-sm font-medium text-brand-700 bg-brand-50 border border-brand-200 rounded-xl hover:bg-brand-100 disabled:opacity-50 flex items-center space-x-2"
                >
                  {testing ? (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <ArrowPathIcon className="h-4 w-4" />
                  )}
                  <span>{testing ? 'Testing...' : 'Test Connection'}</span>
                </button>
                <button
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 disabled:opacity-50 flex items-center space-x-2"
                >
                  {disconnecting ? (
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  ) : (
                    <TrashIcon className="h-4 w-4" />
                  )}
                  <span>{disconnecting ? 'Disconnecting...' : 'Disconnect'}</span>
                </button>
              </div>

              {/* How It Works */}
              <div className="border-t border-slate-100 pt-5">
                <h4 className="text-sm font-semibold text-slate-900 mb-2">How It Works</h4>
                <ul className="space-y-2 text-xs text-slate-600">
                  <li className="flex items-start space-x-2">
                    <span className="w-5 h-5 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">1</span>
                    <span>A customer sends a WhatsApp message to your business number</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-5 h-5 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">2</span>
                    <span>Your AI chatbot processes the message using your Knowledge Base and FAQs</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-5 h-5 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">3</span>
                    <span>The response is sent back via WhatsApp automatically</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <span className="w-5 h-5 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">4</span>
                    <span>Conversations appear in your Inbox, and leads are captured automatically</span>
                  </li>
                </ul>
              </div>
            </div>
          ) : (
            /* ==================== NOT CONNECTED STATE ==================== */
            <div className="space-y-5">
              {/* Connect Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone Number ID</label>
                  <input
                    type="text"
                    value={phoneNumberId}
                    onChange={(e) => setPhoneNumberId(e.target.value)}
                    placeholder="e.g. 106540123456789"
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                  <p className="text-xs text-slate-400 mt-1">Found in your Meta Developer App under WhatsApp &gt; Getting Started</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Permanent Access Token</label>
                  <input
                    type="password"
                    value={accessToken}
                    onChange={(e) => setAccessToken(e.target.value)}
                    placeholder="EAA..."
                    className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                  />
                  <p className="text-xs text-slate-400 mt-1">Generated from Meta Business Settings &gt; System Users</p>
                </div>
                <button
                  onClick={handleConnect}
                  disabled={saving || !phoneNumberId.trim() || !accessToken.trim()}
                  className="w-full px-4 py-2.5 bg-brand-600 text-white text-sm font-medium rounded-xl hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {saving ? (
                    <>
                      <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      <span>Connecting...</span>
                    </>
                  ) : (
                    <span>Connect WhatsApp</span>
                  )}
                </button>
              </div>

              {/* Webhook URL */}
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-xs text-slate-500 font-medium mb-2">Webhook URL (paste this in your Meta Developer App)</p>
                <div className="flex items-center space-x-2">
                  <code className="flex-1 text-xs text-slate-700 bg-white border border-slate-200 rounded-lg px-3 py-2 overflow-x-auto">
                    {WEBHOOK_URL}
                  </code>
                  <button
                    onClick={() => copyToClipboard(WEBHOOK_URL, setWebhookCopied)}
                    className="px-3 py-2 text-xs font-medium text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 flex items-center space-x-1 flex-shrink-0"
                  >
                    {webhookCopied ? <CheckIcon className="h-3.5 w-3.5 text-green-500" /> : <ClipboardIcon className="h-3.5 w-3.5" />}
                    <span>{webhookCopied ? 'Copied!' : 'Copy'}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Setup Instructions (Collapsible) */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <button
          onClick={() => setShowSetup(!showSetup)}
          className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <svg className="h-4 w-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="text-left">
              <h3 className="text-sm font-semibold text-slate-900">Setup Guide</h3>
              <p className="text-xs text-slate-500">How to get your Phone Number ID and Access Token from Meta</p>
            </div>
          </div>
          {showSetup ? (
            <ChevronUpIcon className="h-5 w-5 text-slate-400" />
          ) : (
            <ChevronDownIcon className="h-5 w-5 text-slate-400" />
          )}
        </button>

        {showSetup && (
          <div className="px-5 pb-5 border-t border-slate-100">
            <div className="space-y-4 mt-4">
              {/* Prerequisites */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <h4 className="text-xs font-semibold text-amber-800 mb-1">Prerequisites</h4>
                <ul className="text-xs text-amber-700 space-y-1 list-disc list-inside">
                  <li>A Facebook account (personal, used to create the business account)</li>
                  <li>A phone number NOT already registered on WhatsApp personal or Business app</li>
                  <li>Basic business information (name, address, website)</li>
                </ul>
              </div>

              {/* Steps */}
              <div className="space-y-3">
                <SetupStep number={1} title="Create Facebook Business Account">
                  <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside">
                    <li>Go to <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">business.facebook.com</a></li>
                    <li>Click "Create Account"</li>
                    <li>Enter your business name, your name, and business email</li>
                    <li>Follow the verification steps</li>
                  </ol>
                </SetupStep>

                <SetupStep number={2} title="Create Meta Developer App">
                  <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside">
                    <li>Go to <a href="https://developers.facebook.com" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">developers.facebook.com</a></li>
                    <li>Click "My Apps" then "Create App"</li>
                    <li>Select "Other" for use case, then "Business" for app type</li>
                    <li>Name the app (e.g., "My Company Chatbot")</li>
                    <li>Link it to the business account from Step 1</li>
                  </ol>
                </SetupStep>

                <SetupStep number={3} title="Add WhatsApp to the App">
                  <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside">
                    <li>In the app dashboard, click "Add Product"</li>
                    <li>Select "WhatsApp" and click "Set Up"</li>
                    <li>Select the business account when prompted</li>
                  </ol>
                </SetupStep>

                <SetupStep number={4} title="Add a Phone Number">
                  <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside">
                    <li>In WhatsApp &gt; Getting Started, click "Add Phone Number"</li>
                    <li>Enter the business display name and phone number</li>
                    <li>Verify the phone number with the OTP sent via SMS or voice call</li>
                    <li>Note down the <strong>Phone Number ID</strong> shown after verification</li>
                  </ol>
                </SetupStep>

                <SetupStep number={5} title="Generate Permanent Access Token">
                  <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside">
                    <li>Go to <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">business.facebook.com</a> &gt; Business Settings &gt; System Users</li>
                    <li>Click "Add" to create a new system user</li>
                    <li>Set the role to "Admin"</li>
                    <li>Click "Generate Token"</li>
                    <li>Select the WhatsApp app from Step 2</li>
                    <li>Check permissions: <code className="bg-slate-100 px-1 rounded text-[10px]">whatsapp_business_messaging</code> and <code className="bg-slate-100 px-1 rounded text-[10px]">whatsapp_business_management</code></li>
                    <li>Click "Generate Token" and copy the <strong>Access Token</strong></li>
                  </ol>
                </SetupStep>

                <SetupStep number={6} title="Set Up Webhook in Meta">
                  <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside">
                    <li>In your Meta Developer App, go to WhatsApp &gt; Configuration</li>
                    <li>Click "Edit" on the Webhook section</li>
                    <li>Paste the Webhook URL shown above</li>
                    <li>Set a Verify Token (any random string you choose)</li>
                    <li>Subscribe to the "messages" webhook field</li>
                  </ol>
                </SetupStep>

                <SetupStep number={7} title="Connect in CognaAssist">
                  <ol className="text-xs text-slate-600 space-y-1 list-decimal list-inside">
                    <li>Enter the <strong>Phone Number ID</strong> from Step 4 above</li>
                    <li>Enter the <strong>Access Token</strong> from Step 5 above</li>
                    <li>Click "Connect WhatsApp"</li>
                    <li>The system will verify the connection automatically</li>
                  </ol>
                </SetupStep>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}


function SetupStep({ number, title, children }) {
  return (
    <div className="flex items-start space-x-3">
      <span className="w-6 h-6 bg-brand-100 text-brand-700 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
        {number}
      </span>
      <div className="flex-1">
        <h5 className="text-sm font-medium text-slate-900 mb-1">{title}</h5>
        {children}
      </div>
    </div>
  )
}
